import { IOrderRegistration } from '../type/type';
import KoreanLunarCalendar from 'korean-lunar-calendar';
import { removeBracketContent } from './util';

const calendar = new KoreanLunarCalendar();

// 주문등록 유효성 검사
export const validationOrderRegistration = (
  data: IOrderRegistration[],
  products: {
    productCode: string;
    productName: string;
    custInfoCount: number;
  }[]
) => {
  if (data.length === 0) {
    return null;
  }

  const errors: Record<number, Record<string, string>> = {};

  data.forEach((row, index) => {
    const rowErrors: Record<string, string> = {};

    // 주문정보
    const productCodeOrName = row['productCodeOrName'];
    const email = row['email'];
    const telNo = row['telNo'];

    // 고객정보1
    const name1 = row['name1'];
    const solarType1 = row['solarType1'];
    const year1 = parseInt(row['year1'] as any);
    const month1 = parseInt(row['month1'] as any);
    const day1 = parseInt(row['day1'] as any);
    const hour1 = row['hour1'] as any;
    const minute1 = row['minute1'] as any;
    const sex1 = row['sex1'];

    // 고객정보2
    const name2 = row['name2'];
    const solarType2 = row['solarType2'];
    const year2 = parseInt(row['year2'] as any);
    const month2 = parseInt(row['month2'] as any);
    const day2 = parseInt(row['day2'] as any);
    const hour2 = row['hour2'] as any;
    const minute2 = row['minute2'] as any;
    const sex2 = row['sex2'];

    // 1. productCodeOrName 유효성 검사
    if (!productCodeOrName) {
      rowErrors['productCodeOrName'] = '상품 이름(또는 코드)을 꼭 써 주세요.';
    } else {
      // 콤마로 구분된 상품코드 또는 상품명 처리
      const productCodes = productCodeOrName
        .split(',')
        .map(code => code.trim());

      // 각 상품코드가 유효한지 확인
      const invalidProducts = productCodes.filter(code => {
        // 빈 문자열은 건너뛰기
        if (!code) return false;

        // 대괄호 내용 제거한 상품명으로도 비교
        const cleanedCode = removeBracketContent(code);

        return !products.some(
          product =>
            product.productCode === code ||
            product.productName === code ||
            product.productCode === cleanedCode ||
            product.productName === cleanedCode
        );
      });

      if (invalidProducts.length > 0) {
        rowErrors[
          'productCodeOrName'
        ] = `우리 시스템에 없는 상품이에요: ${invalidProducts.join(', ')}`;
      }
    }

    // 1.5. telNo (연락처) 또는 email 둘 중 하나는 필수
    const telPattern = /^(01[016789])-\d{3,4}-\d{4}$/; // 한국 전화번호에서 하이픈 필수
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // 둘 다 없으면 에러 처리
    if (!telNo && !email) {
      rowErrors['telNo'] = '전화번호나 이메일 중 한 개는 반드시 써 주세요.';
      rowErrors['email'] = '전화번호나 이메일 중 한 개는 반드시 써 주세요.';
    } else {
      // 전화번호가 입력되어 있다면 검증
      if (telNo) {
        if (!telPattern.test(telNo)) {
          rowErrors['telNo'] = '전화번호는 010-1234-5678처럼 - 를 넣어 주세요.';
        }
      }

      // 이메일이 입력되어 있다면 검증
      if (email) {
        if (!emailPattern.test(email)) {
          rowErrors['email'] =
            '이메일은 이름@회사.컴 처럼 @와 . 이 들어가야 해요.';
        }
      }
    }

    // 3. 고객정보1 유효성 검사
    if (!name1) {
      rowErrors['name1'] = '첫 번째 사람 이름을 써 주세요.';
    } else {
      // 한글과 한자만 허용하는 정규식
      const namePattern = /^[가-힣\u4E00-\u9FFF\u3400-\u4DBF\uF900-\uFAFF]+$/;
      if (!namePattern.test(name1)) {
        rowErrors['name1'] = '이름은 한글이나 한자만 써 주세요.';
      }
    }

    if (!solarType1) {
      rowErrors['solarType1'] =
        "양력인지 음력인지 입력해 주세요. 윤달이면 '음력(윤달)'을 입력해 주세요.";
    } else if (
      solarType1 !== '양력' &&
      solarType1 !== '음력' &&
      solarType1 !== '음력(윤달)'
    ) {
      rowErrors['solarType1'] = '양력/음력/음력(윤달) 중에서 입력해 주세요.';
    }

    if (!year1 && year1 !== 0) {
      rowErrors['year1'] = '첫 번째 사람 생년을 써 주세요.';
    } else if (year1 < 1900) {
      rowErrors['year1'] = '생년은 1900년 이후만 입력 가능합니다.';
    }
    if (!month1 && month1 !== 0) {
      rowErrors['month1'] = '첫 번째 사람 생월을 써 주세요.';
    }
    if (!day1 && day1 !== 0) {
      rowErrors['day1'] = '첫 번째 사람 생일을 써 주세요.';
    }

    // 달력 유효성 체크
    if (solarType1 === '양력') {
      // Validate solar date using standard checks
      const validDate = calendar.setSolarDate(year1, month1, day1);
      if (!validDate) {
        rowErrors['year1'] = '달력에 없는 날짜예요. 다시 확인해 주세요.';
        rowErrors['month1'] = '달력에 없는 날짜예요. 다시 확인해 주세요.';
        rowErrors['day1'] = '달력에 없는 날짜예요. 다시 확인해 주세요.';
      }
    } else if (solarType1 === '음력') {
      // Validate lunar date using the library
      const lunarValid = calendar.setLunarDate(year1, month1, day1, false); // 윤달 여부는 false로 설정
      if (!lunarValid) {
        rowErrors['year1'] = '달력에 없는 날짜예요. 다시 확인해 주세요.';
        rowErrors['month1'] = '달력에 없는 날짜예요. 다시 확인해 주세요.';
        rowErrors['day1'] = '달력에 없는 날짜예요. 다시 확인해 주세요.';
      }
    }

    //
    // ** 생시 / 생분: 둘 다 있거나 둘 다 없거나 **
    // 하나만 입력되고 다른 하나는 비어있으면 에러 처리
    //
    const isHour1Empty = hour1 === undefined || hour1 === null || hour1 === '';
    const isMinute1Empty =
      minute1 === undefined || minute1 === null || minute1 === '';

    // "둘 다 비어있지 않다면 => 둘 다 있어야 한다" 로직
    // (1) hour1만 있고 minute1 없는 경우
    if (!isHour1Empty && isMinute1Empty) {
      rowErrors['minute1'] = '시간과 분은 둘 다 쓰거나, 둘 다 비워 두세요.';
    }
    // (2) minute1만 있고 hour1 없는 경우
    if (!isMinute1Empty && isHour1Empty) {
      rowErrors['hour1'] = '시간과 분은 둘 다 쓰거나, 둘 다 비워 두세요.';
    }

    // 생시/생분이 모두 있을 때만 범위 검사
    if (!isHour1Empty && !isMinute1Empty) {
      const hour1Num = parseInt(hour1 as any);
      if (isNaN(hour1Num)) {
        rowErrors['hour1'] = '시간은 숫자로 써 주세요.';
      } else if (hour1Num < 0 || hour1Num > 23) {
        rowErrors['hour1'] = '시간은 0~23 사이로 써 주세요.';
      }

      const minute1Num = parseInt(minute1 as any);
      if (isNaN(minute1Num)) {
        rowErrors['minute1'] = '분은 숫자로 써 주세요.';
      } else if (minute1Num < 0 || minute1Num > 59) {
        rowErrors['minute1'] = '분은 0~59 사이로 써 주세요.';
      }
    }

    if (!sex1) {
      rowErrors['sex1'] = '남성 또는 여성을 입력해 주세요.';
    } else if (sex1 !== '남성' && sex1 !== '여성') {
      rowErrors['sex1'] = '남성/여성만 입력할 수 있어요.';
    }

    // 4. 고객정보2 유효성 검사
    const selectedProduct = products.find(
      product =>
        product.productCode === productCodeOrName ||
        product.productName === productCodeOrName
    );

    // 고객정보2가 필요한 상품인 경우 (custInfoCount >= 2)
    if (selectedProduct && selectedProduct.custInfoCount >= 2) {
      if (!name2) {
        rowErrors['name2'] = '두 번째 사람 이름을 써 주세요.';
      } else {
        // 한글과 한자만 허용하는 정규식
        const namePattern = /^[가-힣\u4E00-\u9FFF\u3400-\u4DBF\uF900-\uFAFF]+$/;
        if (!namePattern.test(name2)) {
          rowErrors['name2'] = '이름은 한글이나 한자만 써 주세요.';
        }
      }

      if (!solarType2) {
        rowErrors['solarType2'] = '양력/음력/음력(윤달) 중에서 입력해 주세요.';
      } else if (
        solarType2 !== '양력' &&
        solarType2 !== '음력' &&
        solarType2 !== '음력(윤달)'
      ) {
        rowErrors['solarType2'] = '양력/음력/음력(윤달) 중에서 입력해 주세요.';
      }

      if (!year2 && year2 !== 0) {
        rowErrors['year2'] = '두 번째 사람 생년을 써 주세요.';
      } else if (year2 < 1900) {
        rowErrors['year2'] = '생년은 1900년 이후만 입력 가능합니다.';
      }
      if (!month2 && month2 !== 0) {
        rowErrors['month2'] = '두 번째 사람 생월을 써 주세요.';
      }
      if (!day2 && day2 !== 0) {
        rowErrors['day2'] = '두 번째 사람 생일을 써 주세요.';
      }

      if (solarType2 === '양력') {
        // Validate solar date
        const validDate = calendar.setSolarDate(year2, month2, day2);
        if (!validDate) {
          rowErrors['year2'] = '달력에 없는 날짜예요. 다시 확인해 주세요.';
          rowErrors['month2'] = '달력에 없는 날짜예요. 다시 확인해 주세요.';
          rowErrors['day2'] = '달력에 없는 날짜예요. 다시 확인해 주세요.';
        }
      } else if (solarType2 === '음력') {
        // Validate lunar date
        const lunarValid = calendar.setLunarDate(year2, month2, day2, false);
        if (!lunarValid) {
          rowErrors['year2'] = '달력에 없는 날짜예요. 다시 확인해 주세요.';
          rowErrors['month2'] = '달력에 없는 날짜예요. 다시 확인해 주세요.';
          rowErrors['day2'] = '달력에 없는 날짜예요. 다시 확인해 주세요.';
        }
      }

      //
      // **생시/생분 2: 둘 다 있거나 둘 다 없거나**
      //
      const isHour2Empty =
        hour2 === undefined || hour2 === null || hour2 === '';
      const isMinute2Empty =
        minute2 === undefined || minute2 === null || minute2 === '';

      // (1) hour2만 있고 minute2 없는 경우
      if (!isHour2Empty && isMinute2Empty) {
        rowErrors['minute2'] = '시간과 분은 둘 다 쓰거나, 둘 다 비워 두세요.';
      }
      // (2) minute2만 있고 hour2 없는 경우
      if (!isMinute2Empty && isHour2Empty) {
        rowErrors['hour2'] = '시간과 분은 둘 다 쓰거나, 둘 다 비워 두세요.';
      }

      // 생시/생분이 둘 다 있을 때만 범위 검사
      if (!isHour2Empty && !isMinute2Empty) {
        const hour2Num = parseInt(hour2 as any);
        if (isNaN(hour2Num)) {
          rowErrors['hour2'] = '시간은 숫자로 써 주세요.';
        } else if (hour2Num < 0 || hour2Num > 23) {
          rowErrors['hour2'] = '시간은 0~23 사이로 써 주세요.';
        }

        const minute2Num = parseInt(minute2 as any);
        if (isNaN(minute2Num)) {
          rowErrors['minute2'] = '분은 숫자로 써 주세요.';
        } else if (minute2Num < 0 || minute2Num > 59) {
          rowErrors['minute2'] = '분은 0~59 사이로 써 주세요.';
        }
      }

      if (!sex2) {
        rowErrors['sex2'] = '남성 또는 여성을 입력해 주세요.';
      } else if (sex2 !== '남성' && sex2 !== '여성') {
        rowErrors['sex2'] = '남성/여성만 입력할 수 있어요.';
      }
    }

    // 5. 고객정보2가 정통사주(= custInfoCount=1)일 경우, 고객정보2는 모두 빈 값이어야 함
    if (selectedProduct && selectedProduct.custInfoCount === 1) {
      if (
        name2 ||
        solarType2 ||
        year2 ||
        month2 ||
        day2 ||
        hour2 ||
        minute2 ||
        sex2
      ) {
        const secondArr = [
          'name2',
          'solarType2',
          'year2',
          'month2',
          'day2',
          'hour2',
          'minute2',
          'sex2',
        ];
        secondArr.forEach(item => {
          rowErrors[
            item
          ] = `${selectedProduct.productName}은 한 사람만 입력하면 되는 상품이라 두 번째 정보는 비워 두셔야 해요.`;
        });
      }
    }

    // row에 에러가 있을 경우만 errors 객체에 추가
    if (Object.keys(rowErrors).length > 0) {
      errors[index] = rowErrors;
    }
  });

  return errors;
};
